import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useMatch } from "react-router-dom";

import {
  AboutTheRoomDetails,
  AboutTheRoomRow,
  AboutTheRoomSection,
  DetailsSection,
  HostDetailsAbout,
  HostDetailsInfoSection,
  HostDetailsWrapper,
  LeftColumnAbout,
  Location,
  MainBody,
  MainWrapper,
  MapWithRoomImage,
  MapWithRoomRoot,
  RightColumnAbout,
  RoomDetailsDescription,
  RoomDetailsSection,
  RoomDetailsSectionNew,
  RoomNavigationSection,
  NavigationButton,
  RoomInfo,
  RoomTitle,
  CityName,
  SectionTitle,
  SocialPreferensecRange,
  TypeOfRoom,
  TitleH2,
  TitleH3,
  TitleH4,
  ImportantValuesStyle,
  HostInfoWrapper,
  HostDetailsStyle,
  HostName,
  AgeGender,
  Activity,
  HouseholdType,
  ImageContainer,
  PriceWrapper,
  ChatContainer,
  DateWrapper,
  CostHoverWrapper,
  CostContainerMobile,
  DepositWrapper,
  StyledHR,
  CostLine,
  PriceText,
  PriceValue,
  CostContainer,
  MapWrapper,
  StickyFooterWrapper,
  InfoWrapper,
  CompleteWidthDiv,
  HalfWidthDiv
} from "./styles";
import { InfoMessage } from "../HostHouseRegistration/RoomsDetails/styles";
import { Button, IcoArrowLeft, IcoInfo } from "components/generic";
import { SocialPreferences } from "pages/ProfileUser/SimpleComponents";
import { useRange } from "components/Range";
import { Carousel } from "./Carousel";
import { HostVerify } from "pages/ProfileUserEdit/HostVerified";
import {
  IHouse,
  IMatch,
  IUserShort,
  StudentMatchCharacteristicsValues,
  HouseAmountOfPeopleValues,
  HouseSecurityItemsValues,
  HouseOtherItemsAndServicesValues,
  HouseAccommodationValues,
  THouseAccommodation,
  HouseTenantCharacteristicsValues,
  HouseTenantDescriptionValues,
  HousePetsValues
} from "interfaces/interfaces";
import {
  MONTHS,
  ROOM_INFO_ROUTE,
  PRIVATE_USE,
  SHARED_USE,
  TYPE_ROOM
} from "utils/constants";
import { theme } from "styles/theme";
import { UserAPI } from "API/userApi";
import { LoaderCustom } from "components/Loader";
import { useContextHospi } from "../../context/ContextHospi";
import { Button as AntdButton, Skeleton } from "antd";
import { ShowInterestModal } from "../../components/ShowInterestModal";
import { useGetMatch } from "../../API/queries/studentApiQueries";
import { activeYears, useIsHost } from "../../utils/helpers";
import { HouseAPI } from "../../API/houseApi";
import {
  generateGoogleMapsUrl,
  generateStaticMapImageUrl
} from "../../components/Map/utils";
import { getHouseName } from "../../utils/house";
import { getLanguage } from "../../utils/fetchMapInfo";
import { TLang } from "../../assets/data/isoNameLanguage";
import { toNumber } from "lodash";
import IconFurnished from "assets/images/wizard/Accommodation_Furnished.png";
import IconNonFurnished from "assets/images/wizard/Accommodation_NonFurnished.png";
import { useIsMobile } from "utils/hooks/useWindowSize";

function calculateAge(birthDateString: String) {
  const birthDate = new Date(birthDateString.toString());
  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();

  // If the birth month hasn't occurred yet this year or it's the current month but the day hasn't occurred yet, subtract 1 from the age.
  if (
    monthDiff < 0 ||
    (monthDiff === 0 && today.getDate() < birthDate.getDate())
  ) {
    age--;
  }
  return age;
}

export const DetailedRoomInfo: React.FC<{
  roomId?: string;
  setShowRoomPreview?: (show: boolean) => void;
}> = ({ roomId, setShowRoomPreview }) => {
  const [room, setRoom] = useState<Partial<IHouse> | undefined>(undefined);
  const [host, setHost] = useState<Partial<IUserShort>>({});
  const { userProfile } = useContextHospi();
  const [showInterestModal, setShowInterestModal] = useState(false);
  const isHost = useIsHost();

  const { t } = useTranslation("student");
  const routeMatch = useMatch(ROOM_INFO_ROUTE + "/:id"); // useParams not working here by some reason
  const navigate = useNavigate();

  const { data: match, isLoading: isLoadingMatch } = useGetMatch(
    userProfile?.user_id,
    host.user_id,
    room?.house_id,
    !isHost
  );

  useEffect(() => {
    const currentRoomId = roomId ? roomId : routeMatch?.params?.id;
    if (!currentRoomId) {
      return;
    }
    HouseAPI.getHouse(currentRoomId).then((house) => {
      setRoom(house);
    });
  }, [roomId, routeMatch?.params?.id]);

  useEffect(() => {
    if (room?.user_id !== undefined) {
      UserAPI.getUserPreview(room?.user_id).then((user) => {
        setHost(user);
      });
    }
  }, [room]);

  const roomPhotos = useMemo(() => {
    return room?.photos?.sort((a, b) => a.order - b.order) ?? [];
  }, [room?.photos]);

  const roomLocation = useMemo(() => {
    if (room?.latitude !== undefined && room?.longitude !== undefined) {
      return {
        lat: room.latitude,
        lng: room.longitude
      };
    }
    return null;
  }, [room?.latitude, room?.longitude]);

  const houseTitle =
    room?.kind && host.user_id
      ? getHouseName(room.kind, {
          first_name: host.first_name!,
          last_name: host.last_name!
        })
      : "";

  const isMobile = useIsMobile();
  return (
    <MainWrapper>
      {setShowRoomPreview && (
        <button id="handle-close" onClick={() => setShowRoomPreview(false)}>
          &#10005;
        </button>
      )}
      {!room || !host.user_id ? (
        <LoaderCustom
          size={5}
          color={theme.colors.bgBody}
          text={t("textLoading.0")}
        />
      ) : (
        <>
          {!isHost ? (
            <RoomNavigation
              title={houseTitle}
              street={room.street ?? ""}
              city={room.city ?? ""}
              house_status={room.house_status ?? undefined}
              verification_status={host.verification_status ?? undefined}
            />
          ) : null}
          {roomPhotos.length ? <Carousel photos={roomPhotos} /> : ""}
          {!isMobile ? (
            <MainBody>
              <LeftColumnAbout>
                <DetailsSection>
                  <RoomDetailsNew
                    amount_of_people={room.amount_of_people}
                    kind={room.kind}
                    accommodation={room.accommodation}
                    bedroom_area={room.bedroom_area ?? null}
                    house_area={room.house_area ?? null}
                    shared_items={room.shared_items ?? []}
                    private_items={room.private_items ?? []}
                    other_items_and_services={room.other_items_and_services}
                    security_items={room.security_items}
                  />
                  <ImportantValues
                    tenant_characteristics={room.tenant_characteristics}
                    tenant_description={room.tenant_description}
                  />
                  <AboutHouseHold
                    household_description={room.household_description}
                    people_living={room.people_living}
                    pets={room.pets}
                    spoken_languages={room.spoken_languages ?? []}
                    uniqueness_accommodation={room.uniqueness_accommodation}
                    description={room.description ?? ""}
                  />
                </DetailsSection>
              </LeftColumnAbout>
              <RightColumnAbout>
                {host === undefined ? (
                  ""
                ) : (
                  <>
                    <AboutTheHost
                      first_name={host.first_name}
                      gender={host.gender}
                      birth_date={host.birth_date}
                      active_years={host.active_years ?? 0}
                      hosted_tenants_count={host.hosted_tenants_count}
                      thumbnail_url={host.thumbnail_url}
                      verification_status={host.verification_status}
                      country={room.country ?? ""}
                      characteristics={room.characteristics}
                    />
                    <AvailabilityAndPrices
                      kind={room.kind}
                      available_from={room.available_from ?? ""}
                      available_to={room.available_to ?? ""}
                      price={room.price ?? "0"}
                      additional_expenses={room.additional_expenses ?? "0"}
                      service_costs={room.service_costs ?? "0"}
                      deposit={room.deposit ?? "0"}
                      first_name={host.first_name}
                      house_status={room.house_status ?? undefined}
                      displayShowInterestButton={!isHost}
                      match={match}
                      isLoadingMatch={isLoadingMatch}
                      onShowInterestClick={() => {
                        setShowInterestModal(true);
                      }}
                    />
                  </>
                )}
              </RightColumnAbout>
            </MainBody>
          ) : (
            <MainBody>
              <LeftColumnAbout>
                <DetailsSection>
                  <RoomDetailsNew
                    amount_of_people={room.amount_of_people}
                    kind={room.kind}
                    accommodation={room.accommodation}
                    bedroom_area={room.bedroom_area ?? null}
                    house_area={room.house_area ?? null}
                    shared_items={room.shared_items ?? []}
                    private_items={room.private_items ?? []}
                    other_items_and_services={room.other_items_and_services}
                    security_items={room.security_items}
                  />
                </DetailsSection>
                <AboutTheHost
                  first_name={host.first_name}
                  gender={host.gender}
                  birth_date={host.birth_date}
                  active_years={host.active_years ?? 0}
                  hosted_tenants_count={host.hosted_tenants_count}
                  thumbnail_url={host.thumbnail_url}
                  verification_status={host.verification_status}
                  country={room.country ?? ""}
                  characteristics={room.characteristics}
                />
                <ImportantValues
                  tenant_characteristics={room.tenant_characteristics}
                  tenant_description={room.tenant_description}
                />
                <AboutHouseHold
                  household_description={room.household_description}
                  people_living={room.people_living}
                  pets={room.pets}
                  spoken_languages={room.spoken_languages ?? []}
                  uniqueness_accommodation={room.uniqueness_accommodation}
                  description={room.description ?? ""}
                />
                <AvailabilityAndPricesMobile
                  price={room.price ?? "0"}
                  additional_expenses={room.additional_expenses ?? "0"}
                  service_costs={room.service_costs ?? "0"}
                  deposit={room.deposit ?? "0"}
                />
              </LeftColumnAbout>
            </MainBody>
          )}

          {roomLocation ? (
            <MapWithRoom location={roomLocation} zoom={15} />
          ) : null}
        </>
      )}
      {!isMobile ? (
        <Button
          main
          style={{
            alignSelf: "flex-end",
            margin: "1rem",
            padding: "16px 40px",
            fontSize: "18px"
          }}
          onClick={() => {
            if (setShowRoomPreview) {
              setShowRoomPreview(false);
            } else {
              window.history.length > 1 ? navigate(-1) : window.close();
            }
          }}
        >
          {t("buttonClose")}
        </Button>
      ) : null}

      {showInterestModal && host?.user_id && room ? (
        <ShowInterestModal
          host={host}
          house={room}
          isShow={true}
          onClose={() => {
            setShowInterestModal(false);
          }}
          onSent={() => {
            setShowInterestModal(false);
          }}
        />
      ) : null}
      {isMobile ? (
        <StickyFooter
          kind={room?.kind}
          available_from={room?.available_from ?? ""}
          available_to={room?.available_to ?? ""}
          price={room?.price ?? "0"}
          additional_expenses={room?.additional_expenses ?? "0"}
          service_costs={room?.service_costs ?? "0"}
          deposit={room?.deposit ?? "0"}
          first_name={host.first_name}
          house_status={room?.house_status ?? undefined}
          displayShowInterestButton={!isHost}
          match={match}
          isLoadingMatch={isLoadingMatch}
          onShowInterestClick={() => {
            setShowInterestModal(true);
          }}
        />
      ) : null}
    </MainWrapper>
  );
};

// NAVIGATION //

const StickyFooter: React.FC<
  Pick<
    IHouse,
    | "available_from"
    | "available_to"
    | "price"
    | "deposit"
    | "additional_expenses"
    | "service_costs"
    | "kind"
    | "house_status"
  > &
    Pick<IUserShort, "first_name"> & {
      onShowInterestClick: () => void;
      displayShowInterestButton: boolean;
      match?: IMatch;
      isLoadingMatch: boolean;
    }
> = ({
  available_from,
  available_to,
  price,
  deposit,
  additional_expenses,
  service_costs,
  first_name,
  kind,
  displayShowInterestButton,
  onShowInterestClick,
  match,
  isLoadingMatch,
  house_status
}) => {
  const { t: t_calendar } = useTranslation("translation");
  const { t } = useTranslation("hostrooms");
  const { t: tHouse } = useTranslation("house");
  const { t: tStudent } = useTranslation("student");

  const dateLine = (value: string | undefined): string => {
    if (value === undefined || value === null) return "";
    const dt = new Date(value);
    return `${dt.getDate()} ${t_calendar(
      `calendar.months_short.${MONTHS[dt.getMonth()]}`
    ).toLocaleLowerCase()} ${dt.getFullYear()}`;
  };
  const totalPrice =
    (toNumber(price) || 0) +
    (toNumber(additional_expenses) || 0) +
    (toNumber(service_costs) || 0);

  return (
    <StickyFooterWrapper>
      <InfoWrapper>
        <DateWrapper>
          <TitleH3 fontSize="14px">
            {available_from ? dateLine(available_from) : "-"}
          </TitleH3>
          <TitleH3 fontSize="14px">
            {available_to === undefined
              ? `- ${t("anyDate")}`
              : available_to
              ? `- ${dateLine(available_to)}`
              : `- ${t("anyDate")}`}
          </TitleH3>
        </DateWrapper>
        <TitleH4 fontSize="14px">
          € {totalPrice} {tHouse("wizard.steps.Prices.form.price.title")}{" "}
        </TitleH4>
      </InfoWrapper>
      <ChatContainer $isSmallOnMobile>
        {isLoadingMatch ? <Skeleton.Button size="large" active /> : null}
        {!isLoadingMatch && displayShowInterestButton && match ? (
          <>
            {match.match_status === undefined &&
            house_status === "PUBLISHED" ? (
              <AntdButton
                type="primary"
                size="large"
                onClick={onShowInterestClick}
              >
                {tStudent("roomNavigationMobile.sendChat")}
              </AntdButton>
            ) : house_status !== "PUBLISHED" ? (
              <AntdButton type="default" size="large" disabled>
                {tStudent("roomNavigationMobile.notAvailable")}
              </AntdButton>
            ) : (
              <AntdButton type="primary" size="large" disabled>
                {tStudent("roomNavigationMobile.chatRequestSent", {
                  name: first_name
                })}
              </AntdButton>
            )}
          </>
        ) : null}
      </ChatContainer>
    </StickyFooterWrapper>
  );
};

const RoomNavigation: React.FC<
  Pick<IHouse, "street" | "city" | "house_status"> &
    Pick<IUserShort, "verification_status"> & { title: string }
> = ({ title, city, house_status, verification_status }) => {
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  return (
    <RoomNavigationSection>
      <NavigationButton onClick={() => navigate(-1)}>
        <IcoArrowLeft size="16px" color="#032565" />
        <span>My rooms</span>
      </NavigationButton>
      {!isMobile ? (
        <RoomInfo>
          <RoomTitle>
            <span>{title}</span>
            <HostVerify verification_status={"verified"} showLabel={false} />
          </RoomTitle>
          <CityName>{city}</CityName>
        </RoomInfo>
      ) : null}
    </RoomNavigationSection>
  );
};

// LEFT COLUMN //

const RoomDetailsNew: React.FC<
  Pick<
    IHouse,
    | "amount_of_people"
    | "kind"
    | "accommodation"
    | "bedroom_area"
    | "house_area"
    | "shared_items"
    | "private_items"
    | "other_items_and_services"
    | "security_items"
  >
> = ({
  amount_of_people,
  kind,
  accommodation,
  bedroom_area,
  house_area,
  shared_items,
  private_items,
  other_items_and_services,
  security_items
}) => {
  const { t } = useTranslation("hostrooms");
  const { t: thouse } = useTranslation("house");
  let people_count;
  if (amount_of_people === "one") {
    people_count = t("hostRoomOverview.people_count_single");
  } else {
    people_count = t("hostRoomOverview.people_count_plurar");
  }

  const ACCOMMODATION_ICONS: {
    [key in THouseAccommodation]: React.ReactNode;
  } = {
    furnished: <img src={IconFurnished} alt="" />,
    non_furnished: <img src={IconNonFurnished} alt="" />
  };

  const spaceTypeOptions = TYPE_ROOM.map((v) => ({
    value: v,
    label: thouse(`wizard.steps.SpaceType.form.kind.options.${v}`),
    description: thouse(
      `wizard.steps.SpaceType.form.kind.options_description.${v}`
    )
  }));

  const amountOfPeopleOptions = HouseAmountOfPeopleValues.map((c) => ({
    value: c,
    label: thouse(`wizard.steps.AboutSpace.form.amount_of_people.options.${c}`)
  }));

  const accommodationOptions = HouseAccommodationValues.map((c) => ({
    value: c,
    label: thouse(`wizard.steps.AboutSpace.form.accommodation.options.${c}`),
    description: thouse(
      `wizard.steps.AboutSpace.form.accommodation.options_description.${c}`
    ),
    icon: ACCOMMODATION_ICONS[c]
  }));

  const privateUseOptions = PRIVATE_USE.map((c) => ({
    value: c,
    label: t(`aboutTheRoom.privateUse.items.${c}.title`)
  }));

  const sharedUseOptions = SHARED_USE.map((c) => ({
    value: c,
    label: t(`aboutTheRoom.sharedUse.items.${c}.title`)
  }));

  const securityItemsOptions = HouseSecurityItemsValues.map((c) => ({
    value: c,
    label: thouse(`wizard.steps.Facilities.form.security_items.options.${c}`)
  }));

  const otherItemsAndServicesOptions = HouseOtherItemsAndServicesValues.map(
    (c) => ({
      value: c,
      label: thouse(
        `wizard.steps.Facilities.form.other_items_and_services.options.${c}`
      )
    })
  );

  return (
    <RoomDetailsSectionNew>
      <HalfWidthDiv>
        <TitleH3>{thouse("wizard.steps.SpaceType.titleOverview")}</TitleH3>
        <TitleH4>
          {spaceTypeOptions.find((o) => kind === o.value)?.label},{" "}
          {
            amountOfPeopleOptions.find((v) => v.value === amount_of_people)
              ?.label
          }{" "}
          {people_count}
        </TitleH4>
      </HalfWidthDiv>
      <HalfWidthDiv>
        <TitleH3>
          {thouse("wizard.steps.AboutSpace.form.accommodation.titleOverview")}
        </TitleH3>
        <TitleH4>
          {" "}
          {accommodationOptions.find((v) => v.value === accommodation)?.label}
        </TitleH4>
      </HalfWidthDiv>
      <HalfWidthDiv>
        <TitleH3>
          {" "}
          {thouse("wizard.steps.AboutSpace.form.rent_area.titleOverview")}
        </TitleH3>
        <TitleH4>{bedroom_area}m2</TitleH4>
      </HalfWidthDiv>
      <HalfWidthDiv>
        <TitleH3>
          {thouse(
            "wizard.steps.AboutSpace.form.entire_house_area.titleOverview"
          )}{" "}
        </TitleH3>
        <TitleH4>{house_area}m2</TitleH4>
      </HalfWidthDiv>
      <HalfWidthDiv>
        <TitleH3>
          {" "}
          {thouse("wizard.steps.AboutSpace.form.private_items.title")}
        </TitleH3>
        <TitleH4>
          {" "}
          {privateUseOptions
            .filter((c) => private_items?.includes(c.value))
            ?.map((o) => o.label)
            .join(", ") ?? ""}
        </TitleH4>
      </HalfWidthDiv>
      <HalfWidthDiv>
        <TitleH3>
          {" "}
          {thouse("wizard.steps.AboutSpace.form.shared_items.title")}
        </TitleH3>
        <TitleH4>
          {" "}
          {sharedUseOptions
            .filter((c) => shared_items?.includes(c.value))
            ?.map((o) => o.label)
            .join(", ") ?? ""}
        </TitleH4>
      </HalfWidthDiv>

      <HalfWidthDiv>
        <TitleH3> {thouse("wizard.steps.Facilities.titleOverview")}</TitleH3>
        <TitleH4>
          {" "}
          {securityItemsOptions
            .filter((c) => security_items?.includes(c.value))
            ?.map((o) => o.label)
            .join(", ") ?? ""}{" "}
        </TitleH4>
      </HalfWidthDiv>
      <HalfWidthDiv>
        <TitleH3>
          {" "}
          {thouse(
            "wizard.steps.Facilities.form.other_items_and_services.title"
          )}
        </TitleH3>
        <TitleH4>
          {" "}
          {otherItemsAndServicesOptions
            .filter((c) => other_items_and_services?.includes(c.value))
            ?.map((o) => o.label)
            .join(", ") ?? ""}{" "}
        </TitleH4>
      </HalfWidthDiv>
    </RoomDetailsSectionNew>
  );
};

const ImportantValues: React.FC<
  Pick<IHouse, "tenant_characteristics" | "tenant_description">
> = ({ tenant_characteristics, tenant_description }) => {
  const { t: thouse } = useTranslation("house");

  if (
    tenant_characteristics &&
    tenant_characteristics.length === 0 &&
    tenant_description &&
    tenant_description.length === 0
  ) {
    return <div></div>;
  }

  const characteristicsOptions = HouseTenantCharacteristicsValues.map((c) => ({
    value: c,
    label: thouse(
      `wizard.steps.IdealTenant.form.tenant_characteristics.options.${c}`
    )
  }));
  const descriptionOptions = HouseTenantDescriptionValues.map((c) => ({
    value: c,
    label: thouse(
      `wizard.steps.IdealTenant.form.tenant_description.options.${c}`
    )
  }));

  return (
    <ImportantValuesStyle>
      {tenant_characteristics && tenant_characteristics.length > 0 ? (
        <div>
          <TitleH3>
            {thouse(
              "wizard.steps.IdealTenant.form.tenant_characteristics.title"
            )}
          </TitleH3>
          <TitleH4>
            {characteristicsOptions
              .filter((c) => tenant_characteristics?.includes(c.value))
              ?.map((o) => o.label)
              .join(", ") ?? ""}{" "}
          </TitleH4>
        </div>
      ) : null}
      {tenant_description && tenant_description.length > 0 ? (
        <div>
          <TitleH3>
            {" "}
            {thouse(
              "wizard.steps.IdealTenant.form.tenant_description.title"
            )}{" "}
          </TitleH3>
          <TitleH4>
            {" "}
            {descriptionOptions
              .filter((c) => tenant_description?.includes(c.value))
              ?.map((o) => o.label)
              .join(", ") ?? ""}{" "}
          </TitleH4>
        </div>
      ) : null}
    </ImportantValuesStyle>
  );
};

const AboutHouseHold: React.FC<
  Pick<
    IHouse,
    | "household_description"
    | "people_living"
    | "pets"
    | "spoken_languages"
    | "uniqueness_accommodation"
    | "description"
  >
> = ({
  household_description,
  people_living,
  pets,
  spoken_languages,
  uniqueness_accommodation,
  description
}) => {
  const { t: thouse, i18n } = useTranslation("house");

  const currentLanguage: TLang = i18n.language.toUpperCase() as TLang;

  const allLanguagesOptions = getLanguage(currentLanguage);

  const accommodationOptions = StudentMatchCharacteristicsValues.map((c) => ({
    value: c,
    label: thouse(`wizard.steps.Uniqueness.form.accommodation.options.${c}`)
  }));

  const petsOptions = HousePetsValues.map((v) => ({
    value: v,
    label: thouse(`wizard.steps.AboutHousehold.form.pets.options.${v}`)
  }));

  return (
    <RoomDetailsSectionNew>
      <CompleteWidthDiv>
        <TitleH3>
          {thouse("wizard.steps.AboutHousehold.form.description.title")}
        </TitleH3>
        <TitleH4>{household_description}</TitleH4>
      </CompleteWidthDiv>
      <HalfWidthDiv>
        <TitleH3>
          {thouse("wizard.steps.AboutHousehold.form.people_living.title")}
        </TitleH3>
        <TitleH4>{people_living?.join(", ") ?? ""}</TitleH4>
      </HalfWidthDiv>
      <HalfWidthDiv>
        <TitleH3>
          {thouse("wizard.steps.AboutHousehold.form.pets.titleOverview")}
        </TitleH3>
        <TitleH4>
          {petsOptions
            .filter((v) => pets?.includes(v.value))
            .map((o) => o.label)
            ?.join(", ")}
        </TitleH4>
      </HalfWidthDiv>

      <HalfWidthDiv>
        <TitleH3>
          {" "}
          {thouse(
            "wizard.steps.AboutHousehold.form.spoken_languages.titleOverview"
          )}
        </TitleH3>
        <TitleH4>
          {" "}
          {allLanguagesOptions
            .filter((c) => spoken_languages?.includes(c.value as string))
            ?.map((o) => o.label)
            .join(", ") ?? ""}
        </TitleH4>
      </HalfWidthDiv>
      <HalfWidthDiv>
        <TitleH3>{thouse("wizard.steps.Uniqueness.titleOverview")}</TitleH3>
        <TitleH4>
          {" "}
          {accommodationOptions
            .filter((c) => uniqueness_accommodation?.includes(c.value))
            ?.map((o) => o.label)
            .join(", ") ?? ""}
        </TitleH4>
      </HalfWidthDiv>

      <CompleteWidthDiv>
        <TitleH3>
          {thouse(
            "wizard.steps.Uniqueness.form.place_description.titleOverview"
          )}
        </TitleH3>
        <TitleH4>{description}</TitleH4>
      </CompleteWidthDiv>
    </RoomDetailsSectionNew>
  );
};

const RoomDetails: React.FC<
  Pick<IHouse, "description" | "kind" | "street" | "city">
> = ({ description, kind, city }) => {
  const { t } = useTranslation("hostrooms");

  return (
    <RoomDetailsSection>
      <SectionTitle>
        <TitleH2>{t("roomsDetails.title")}</TitleH2>
      </SectionTitle>
      <RoomDetailsDescription>{description}</RoomDetailsDescription>
      <TypeOfRoom>
        <TitleH3>{t("roomsDetails.typeRoom.title")}</TitleH3>
        <TitleH4>
          {t(`roomsDetails.typeRoom.items.${kind}.title`)}{" "}
          <InfoMessage
            left="-2em"
            message={t(`roomsDetails.typeRoom.items.${kind}.tooltip`)}
          >
            <IcoInfo size="1.2em" />
          </InfoMessage>
        </TitleH4>
      </TypeOfRoom>
      <Location>
        <div>
          <TitleH3>{t("roomsDetails.cityRoom.title")}</TitleH3>
          <TitleH4>{city}</TitleH4>
        </div>
      </Location>
    </RoomDetailsSection>
  );
};

const AboutTheRoom: React.FC<
  Pick<IHouse, "bedroom_area" | "house_area" | "shared_items" | "private_items">
> = ({ bedroom_area, house_area, shared_items, private_items }) => {
  const { t } = useTranslation("hostrooms");

  return (
    <AboutTheRoomSection>
      <SectionTitle>
        <TitleH2>{t("aboutTheRoom.title")}</TitleH2>
      </SectionTitle>
      <AboutTheRoomRow>
        <div>
          <TitleH3>{t("aboutTheRoom.bedroomSize.title")}</TitleH3>
          <TitleH4>{bedroom_area}</TitleH4>
        </div>
        <div>
          <TitleH3>{t("aboutTheRoom.houseSize.title")}</TitleH3>
          <TitleH4>{house_area}</TitleH4>
        </div>
      </AboutTheRoomRow>
      <AboutTheRoomRow>
        <div>
          <TitleH3>{t("aboutTheRoom.sharedUse.title")}</TitleH3>
          {shared_items?.map((item: string, index: number) => (
            <TitleH4 key={index}>
              {t(`aboutTheRoom.sharedUse.items.${item}.title`)}
            </TitleH4>
          ))}
        </div>
        <div>
          <TitleH3>{t("aboutTheRoom.privateUse.title")}</TitleH3>
          {private_items?.map((item: string, index: number) => (
            <TitleH4 key={index}>
              {t(`aboutTheRoom.privateUse.items.${item}.title`)}
            </TitleH4>
          ))}
        </div>
      </AboutTheRoomRow>
    </AboutTheRoomSection>
  );
};

const CostToolTip = ({
  left,
  total_cost,
  price,
  deposit,
  additional_expenses,
  service_costs
}: {
  left: string;
  total_cost: number;
  price: string | null;
  deposit: string | null;
  additional_expenses: string | null;
  service_costs: string | null;
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const { t } = useTranslation("house");

  return (
    <div
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{ position: "relative" }} // This ensures the tooltip positions relative to this component
    >
      <div>
        <span>€ {total_cost} </span>
        <IcoInfo size="1.2em" />
      </div>
      {isHovered && (
        <CostContainer>
          <TitleH3>{t("wizard.steps.Prices.summary.title")}</TitleH3>
          <CostHoverWrapper>
            <CostLine>
              <PriceText>
                {t("wizard.steps.Prices.form.price.titleOverview")}
              </PriceText>
              <PriceValue> € {price}</PriceValue>
            </CostLine>
            <CostLine>
              <PriceText>
                {" "}
                {t(
                  "wizard.steps.Prices.form.additional_expenses.titleOverview"
                )}
              </PriceText>
              <PriceValue> € {additional_expenses}</PriceValue>
            </CostLine>
            <CostLine>
              <PriceText>
                {" "}
                {t("wizard.steps.Prices.form.service_costs.titleOverview")}
              </PriceText>
              <PriceValue> € {service_costs}</PriceValue>
            </CostLine>
            <StyledHR />
            <CostLine>
              <PriceText>
                {t("wizard.steps.Prices.summary.monthly_total")}
              </PriceText>

              <PriceValue fontSize={"18px"}>
                <strong> € {total_cost} </strong>
              </PriceValue>
            </CostLine>
          </CostHoverWrapper>
          <DepositWrapper>
            <CostLine>
              <PriceText>
                {" "}
                {t("wizard.steps.Prices.form.deposit.titleOverview")}
              </PriceText>
              <PriceValue> € {deposit}</PriceValue>{" "}
            </CostLine>
          </DepositWrapper>
        </CostContainer>
      )}
    </div>
  );
};

const CostMobile = ({
  left,
  total_cost,
  price,
  deposit,
  additional_expenses,
  service_costs
}: {
  left: string;
  total_cost: number;
  price: string | null;
  deposit: string | null;
  additional_expenses: string | null;
  service_costs: string | null;
}) => {
  const { t } = useTranslation("house");

  return (
    <CostContainerMobile>
      <TitleH3>{t("wizard.steps.Prices.summary.title")}</TitleH3>
      <CostHoverWrapper>
        <CostLine>
          <PriceText>
            {t("wizard.steps.Prices.form.price.titleOverview")}
          </PriceText>
          <PriceValue> € {price}</PriceValue>
        </CostLine>
        <CostLine>
          <PriceText>
            {" "}
            {t("wizard.steps.Prices.form.additional_expenses.titleOverview")}
          </PriceText>
          <PriceValue> € {additional_expenses}</PriceValue>
        </CostLine>
        <CostLine>
          <PriceText>
            {" "}
            {t("wizard.steps.Prices.form.service_costs.titleOverview")}
          </PriceText>
          <PriceValue> € {service_costs}</PriceValue>
        </CostLine>
        <StyledHR />
        <CostLine>
          <PriceText>
            {t("wizard.steps.Prices.summary.monthly_total")}
          </PriceText>

          <PriceValue fontSize={"18px"}>
            <strong> € {total_cost} </strong>
          </PriceValue>
        </CostLine>
      </CostHoverWrapper>
      <DepositWrapper>
        <CostLine>
          <PriceText>
            {" "}
            {t("wizard.steps.Prices.form.deposit.titleOverview")}
          </PriceText>
          <PriceValue> € {deposit}</PriceValue>{" "}
        </CostLine>
      </DepositWrapper>
    </CostContainerMobile>
  );
};

export const AvailabilityAndPrices: React.FC<
  Pick<
    IHouse,
    | "available_from"
    | "available_to"
    | "price"
    | "deposit"
    | "additional_expenses"
    | "service_costs"
    | "kind"
    | "house_status"
  > &
    Pick<IUserShort, "first_name"> & {
      onShowInterestClick: () => void;
      displayShowInterestButton: boolean;
      match?: IMatch;
      isLoadingMatch: boolean;
    }
> = ({
  available_from,
  available_to,
  price,
  deposit,
  additional_expenses,
  service_costs,
  first_name,
  kind,
  displayShowInterestButton,
  onShowInterestClick,
  match,
  isLoadingMatch,
  house_status
}) => {
  const { t: t_calendar } = useTranslation("translation");
  const { t } = useTranslation("hostrooms");
  const { t: tHouse } = useTranslation("house");
  const { t: tStudent } = useTranslation("student");

  const dateLine = (value: string | undefined): string => {
    if (value === undefined || value === null) return "";
    const dt = new Date(value);
    return `${dt.getDate()} ${t_calendar(
      `calendar.months.${MONTHS[dt.getMonth()]}`
    ).toLocaleLowerCase()} ${dt.getFullYear()}`;
  };
  const totalPrice =
    (toNumber(price) || 0) +
    (toNumber(additional_expenses) || 0) +
    (toNumber(service_costs) || 0);

  const spaceTypeOptions = TYPE_ROOM.map((v) => ({
    value: v,
    label: tHouse(`wizard.steps.SpaceType.form.kind.options.${v}`),
    description: tHouse(
      `wizard.steps.SpaceType.form.kind.options_description.${v}`
    )
  }));
  const isMobile = useIsMobile();
  return (
    <PriceWrapper>
      <SectionTitle>
        <TitleH2>
          {first_name}'s {spaceTypeOptions.find((o) => kind === o.value)?.label}
        </TitleH2>
      </SectionTitle>
      <AboutTheRoomDetails>
        <AboutTheRoomRow>
          <TitleH4>{t("hostRoomOverview.availability")}</TitleH4>
          <DateWrapper>
            <TitleH3>{available_from ? dateLine(available_from) : "-"}</TitleH3>
            <TitleH3>
              {available_to === undefined
                ? `- ${t("anyDate")}`
                : available_to
                ? `- ${dateLine(available_to)}`
                : `- ${t("anyDate")}`}
            </TitleH3>
          </DateWrapper>
        </AboutTheRoomRow>
        <AboutTheRoomRow>
          <TitleH4>
            {tHouse("wizard.steps.Prices.summary.monthly_total")}
          </TitleH4>
          <TitleH3>
            {!isMobile ? (
              <CostToolTip
                left="-15em"
                total_cost={totalPrice}
                price={price}
                deposit={deposit}
                additional_expenses={additional_expenses}
                service_costs={service_costs}
              />
            ) : (
              <CostMobile
                left="-15em"
                total_cost={totalPrice}
                price={price}
                deposit={deposit}
                additional_expenses={additional_expenses}
                service_costs={service_costs}
              />
            )}
          </TitleH3>
        </AboutTheRoomRow>
        <ChatContainer>
          {isLoadingMatch ? <Skeleton.Button size="large" active /> : null}
          {!isLoadingMatch && displayShowInterestButton && match ? (
            <>
              {match.match_status === undefined &&
              house_status === "PUBLISHED" ? (
                <AntdButton
                  type="primary"
                  size="large"
                  onClick={onShowInterestClick}
                >
                  {tStudent("roomNavigation.sendChat")}
                </AntdButton>
              ) : house_status !== "PUBLISHED" ? (
                <AntdButton type="default" size="large" disabled>
                  {tStudent("roomNavigation.notAvailable")}
                </AntdButton>
              ) : (
                <AntdButton type="primary" size="large" disabled>
                  {tStudent("roomNavigation.chatRequestSent", {
                    name: first_name
                  })}
                </AntdButton>
              )}
            </>
          ) : null}
        </ChatContainer>
      </AboutTheRoomDetails>
    </PriceWrapper>
  );
};

export const AvailabilityAndPricesMobile: React.FC<
  Pick<IHouse, "price" | "deposit" | "additional_expenses" | "service_costs">
> = ({ price, deposit, additional_expenses, service_costs }) => {
  const totalPrice =
    (toNumber(price) || 0) +
    (toNumber(additional_expenses) || 0) +
    (toNumber(service_costs) || 0);

  return (
    <PriceWrapper>
      <AboutTheRoomDetails>
        <AboutTheRoomRow>
          <TitleH3>
            <CostMobile
              left="-15em"
              total_cost={totalPrice}
              price={price}
              deposit={deposit}
              additional_expenses={additional_expenses}
              service_costs={service_costs}
            />
          </TitleH3>
        </AboutTheRoomRow>
      </AboutTheRoomDetails>
    </PriceWrapper>
  );
};

// RIGHT COLUMN //

const HostDetails: React.FC<
  Pick<
    IUserShort,
    | "first_name"
    | "last_name"
    | "active_years"
    | "hosted_tenants_count"
    | "thumbnail_url"
    | "verification_status"
  >
> = ({
  first_name,
  last_name,
  active_years,
  hosted_tenants_count,
  thumbnail_url,
  verification_status
}) => {
  const { t } = useTranslation("host");
  return (
    <HostDetailsWrapper>
      <SectionTitle>
        <TitleH2>{t("aboutHost.title1")}</TitleH2>
        {verification_status &&
        hosted_tenants_count &&
        hosted_tenants_count > 0 ? (
          <HostVerify verification_status={"experienced"} />
        ) : (
          <span />
        )}
        <HostVerify verification_status={"verified"} showLabel={false} />
      </SectionTitle>
      <HostDetailsInfoSection>
        <HostDetailsAbout>
          <TitleH4>{`${first_name} ${last_name}`}</TitleH4>
          <div>
            <TitleH4>
              <strong>{activeYears(active_years)}</strong>{" "}
              {` ${t("aboutHost.yearsActive")}`}
            </TitleH4>
            <TitleH4>
              <strong>{hosted_tenants_count}</strong>{" "}
              {` ${t("aboutHost.timesHosted")}`}
            </TitleH4>
          </div>
        </HostDetailsAbout>
        <img src={thumbnail_url + `?${new Date().toString()}`} alt="" />
      </HostDetailsInfoSection>
    </HostDetailsWrapper>
  );
};

const AboutTheHost: React.FC<
  Pick<
    IUserShort,
    | "first_name"
    | "gender"
    | "birth_date"
    | "active_years"
    | "hosted_tenants_count"
    | "thumbnail_url"
    | "verification_status"
  > &
    Pick<IHouse, "country" | "characteristics">
> = ({
  first_name,
  gender,
  birth_date,
  active_years,
  hosted_tenants_count,
  thumbnail_url,
  country,
  characteristics,
  verification_status
}) => {
  const { t } = useTranslation("host");
  const { t: tHouse } = useTranslation("house");
  const { t: tStudent } = useTranslation("student");
  const { t: tHost } = useTranslation("hostrooms");
  let age;
  if (birth_date) {
    age = calculateAge(birth_date);
  }
  return (
    <HostDetailsWrapper>
      <SectionTitle>
        <TitleH2>{tHost("hostRoomOverview.your_host")}</TitleH2>
      </SectionTitle>
      <HostInfoWrapper>
        <ImageContainer>
          <img src={thumbnail_url + `?${new Date().toString()}`} alt="" />
        </ImageContainer>
        <HostDetailsStyle>
          <HostName>{first_name}</HostName>
          {verification_status &&
          hosted_tenants_count &&
          hosted_tenants_count > 0 ? (
            <HostVerify
              verification_status={"experienced"}
              fontSize="14px"
              customMargin="0rem"
            />
          ) : null}
          <AgeGender>
            {age ? `${age},` : null}{" "}
            {tStudent(
              `studentProfile.aboutMeInfo.form.gender.options.${gender}`
            )}
          </AgeGender>
        </HostDetailsStyle>
      </HostInfoWrapper>
      <HouseholdType>
        {tHouse(
          `wizard.steps.AboutHousehold.form.characteristics.options.${characteristics}`
        )}
      </HouseholdType>
      {/* <Country>From {country}</Country> */}
      <Activity>
        <TitleH4>
          <strong>{activeYears(active_years)}</strong>{" "}
          {` ${t("aboutHost.yearsActive")}`}
        </TitleH4>
        <TitleH4>
          <strong>{hosted_tenants_count}</strong>{" "}
          {` ${t("aboutHost.timesHosted")}`}
        </TitleH4>
      </Activity>
    </HostDetailsWrapper>
  );
};

const SocialPreferencesSection: React.FC<
  Pick<IUserShort, "privacy" | "requested_autonomy">
> = ({ privacy, requested_autonomy }) => {
  const { t } = useTranslation("host");
  const privacyHost = useRange(privacy);
  const newInTown = useRange(requested_autonomy);

  useEffect(() => {
    privacyHost.onChange(privacy ?? 50);
    newInTown.onChange(requested_autonomy ?? 50);
  }, [privacy, requested_autonomy, privacyHost, newInTown]);

  return (
    <HostDetailsWrapper>
      <SectionTitle>
        <TitleH2>{t("hostProfile.socialPreferences.title")}</TitleH2>
      </SectionTitle>
      <SocialPreferensecRange>
        <SocialPreferences
          roleUser="host"
          privacy={privacyHost}
          newInTown={newInTown}
        />
      </SocialPreferensecRange>
    </HostDetailsWrapper>
  );
};

const MapWithRoom: React.FC<{
  location: google.maps.LatLngLiteral;
  zoom: number;
}> = ({ location, zoom }) => {
  const handleClick = () => {
    window.open(generateGoogleMapsUrl({ location, zoom }), "_blank");
  };
  const { t } = useTranslation("host");
  const isMobile = useIsMobile();
  return (
    <MapWrapper>
      <MapWithRoomRoot>
        <div
          style={{
            marginLeft: "16px",
            marginBottom: "10px",
            alignSelf: "flex-start"
          }}
        >
          <TitleH2>{t("hostProfile.mapWithRoom.title")}</TitleH2>
        </div>
        <MapWithRoomImage
          onClick={handleClick}
          src={generateStaticMapImageUrl({
            location,
            zoom,
            width: !isMobile ? 1010 : 400,
            height: 400
          })}
          alt={t("hostProfile.mapWithRoom.imageTitle")}
        />
      </MapWithRoomRoot>
    </MapWrapper>
  );
};
